import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSEO } from '../../hooks/useSEO';
import { generateOrganizationSchema } from '../../utils/seo';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { getYouTubeThumbnail } from '../../lib/youtube';

interface Imovel {
  id: string;
  codigo: string;
  titulo: string;
  tipo: string;
  cidade: string;
  bairro: string;
  endereco?: string;
  preco: number;
  area: number;
  quartos: number;
  banheiros: number;
  vagas: number;
  destaque: boolean;
  slug: string;
  fotos: { url: string }[];
}

interface Video {
  id: string;
  titulo: string;
  youtube_url: string;
  destaque: boolean;
}

export default function HomePage() {
  const navigate = useNavigate(); // <-- added navigation hook
  const [imoveisDestaque, setImoveisDestaque] = useState<Imovel[]>([]);
  const [todosImoveis, setTodosImoveis] = useState<Imovel[]>([]);
  const [videosDestaque, setVideosDestaque] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [favoritos, setFavoritos] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'tours' | 'reels'>('tours');

  // Estados para busca rápida
  const [buscaTipo, setBuscaTipo] = useState('');
  const [buscaCidade, setBuscaCidade] = useState('');
  const [buscaPrecoMax, setBuscaPrecoMax] = useState('');

  // Reels do Instagram (mock - pode ser substituído por dados reais)
  const reelsInstagram = [
    {
      id: '1',
      titulo: 'Tour Apartamento Jardins',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/9eba0135f712d3fbb1f8be496523a61c.jpg',
      url: 'https://www.instagram.com/mateusoliveira.corretor/',
    },
    {
      id: '2',
      titulo: 'Casa na Barra dos Coqueiros',
      thumbnail:
        'https://readdy.ai/api/search-image?query=beautiful%20beach%20house%20exterior%20tour%20video%20thumbnail%20tropical%20garden%20pool%20area%20modern%20architecture%20instagram%20reel%20style%20vertical%20format%20sunny%20day&width=400&height=600&seq=reel-002&orientation=portrait',
      url: 'https://www.instagram.com/mateusoliveira.corretor/',
    },
    {
      id: '3',
      titulo: 'Cobertura Duplex Atalaia',
      thumbnail:
        'https://readdy.ai/api/search-image?query=luxury%20penthouse%20duplex%20interior%20tour%20video%20thumbnail%20rooftop%20terrace%20city%20view%20modern%20design%20instagram%20reel%20style%20vertical%20format%20sunset%20lighting&width=400&height=600&seq=reel-003&orientation=portrait',
      url: 'https://www.instagram.com/mateusoliveira.corretor/',
    },
    {
      id: '4',
      titulo: 'Apartamento Vista Mar',
      thumbnail:
        'https://readdy.ai/api/search-image?query=ocean%20view%20apartment%20interior%20tour%20video%20thumbnail%20balcony%20sea%20view%20modern%20furniture%20bright%20space%20instagram%20reel%20style%20vertical%20format&width=400&height=600&seq=reel-004&orientation=portrait',
      url: 'https://www.instagram.com/mateusoliveira.corretor/',
    },
  ];

  // SEO
  useSEO({
    title: 'Mateus Oliveira Corretor | Imóveis em Aracaju e Barra dos Coqueiros - Sergipe',
    description:
      'Encontre imóveis de médio e alto padrão em Aracaju e Barra dos Coqueiros. Compra, venda e aluguel com atendimento especializado. Avaliação gratuita online. WhatsApp (79) 99988-1002',
    keywords:
      'imóveis aracaju, imóveis barra dos coqueiros, corretor aracaju, comprar casa aracaju, apartamentos sergipe, imóveis sergipe, vender imóvel aracaju, mateus oliveira corretor',
    canonical: window.location.origin + '/',
    ogImage:
      'https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20sergipe%20coastline%20brazil%20with%20beautiful%20beaches%20modern%20buildings%20and%20blue%20ocean%20panoramic%20landscape%20photography%20bright%20sunny%20day%20clear%20sky%20vibrant%20colors%20professional%20real%20estate%20photography&width=1200&height=630&seq=og-home-001&orientation=landscape',
    ogType: 'website',
    schema: generateOrganizationSchema(),
  });

  useEffect(() => {
    loadImoveisDestaque();
    loadTodosImoveis();
    loadVideosDestaque();
    loadFavoritos();
  }, []);

  const loadFavoritos = () => {
    const saved = localStorage.getItem('favoritos');
    if (saved) {
      setFavoritos(JSON.parse(saved));
    }
  };

  const toggleFavorito = (imovelId: string) => {
    let newFavoritos: string[];
    if (favoritos.includes(imovelId)) {
      newFavoritos = favoritos.filter((id) => id !== imovelId);
    } else {
      newFavoritos = [...favoritos, imovelId];
    }
    setFavoritos(newFavoritos);
    localStorage.setItem('favoritos', JSON.stringify(newFavoritos));
  };

  const loadImoveisDestaque = async () => {
    try {
      const { data, error } = await supabase
        .from('imoveis')
        .select(`
          *,
          fotos:imovel_fotos(url)
        `)
        .eq('destaque', true)
        .eq('status', 'disponivel')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImoveisDestaque(data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTodosImoveis = async () => {
    try {
      const { data, error } = await supabase
        .from('imoveis')
        .select('id, titulo, cidade, bairro, endereco, preco, slug')
        .eq('status', 'disponivel')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTodosImoveis(data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis para mapa:', error);
    }
  };

  const loadVideosDestaque = async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('destaque', true)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setVideosDestaque(data || []);
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
    }
  };

  const handleBuscaRapida = () => {
    const params = new URLSearchParams();
    if (buscaTipo) params.append('tipo', buscaTipo);
    if (buscaCidade) params.append('cidade', buscaCidade);
    if (buscaPrecoMax) params.append('precoMax', buscaPrecoMax);

    // Use react‑router's navigate instead of a non‑existent global function
    navigate(`/imoveis?${params.toString()}`);
  };

  // Gerar URL do mapa com marcadores
  const getMapUrl = () => {
    // Centro em Aracaju
    const center = '-10.9472,-37.0731';
    const zoom = '12';

    // Criar marcadores para cada cidade
    // (os marcadores já são incluídos na URL do embed padrão)
    return `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=${center}&zoom=${zoom}&maptype=roadmap`;
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white pt-20">
        {/* 1. Imóveis em Destaque */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                Imóveis em <span className="text-orange-500">Destaque</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Selecionamos os melhores imóveis para você
              </p>
            </motion.div>

            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : imoveisDestaque.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-product-shop>
                  {imoveisDestaque.map((imovel, index) => (
                    <motion.div
                      key={imovel.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <div
                        className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group"
                        data-product-shop
                      >
                        <Link to={`/imovel/${imovel.slug}`} className="block">
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={imovel.fotos?.[0]?.url || 'https://via.placeholder.com/400x300'}
                              alt={imovel.titulo}
                              className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                            />
                            {imovel.destaque && (
                              <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                Destaque
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-orange-500 uppercase">
                                {imovel.tipo}
                              </span>
                              <span className="text-xs text-gray-500">{imovel.codigo}</span>
                            </div>
                            <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                              {imovel.titulo}
                            </h3>
                            <p className="text-gray-600 text-xs mb-3 flex items-center">
                              <i className="ri-map-pin-line mr-1"></i>
                              {imovel.bairro}, {imovel.cidade}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-3 pb-3 border-b">
                              <span className="flex items-center">
                                <i className="ri-hotel-bed-line mr-1"></i>
                                {imovel.quartos}
                              </span>
                              <span className="flex items-center">
                                <i className="ri-shower-line mr-1"></i>
                                {imovel.banheiros}
                              </span>
                              <span className="flex items-center">
                                <i className="ri-car-line mr-1"></i>
                                {imovel.vagas}
                              </span>
                              <span className="flex items-center">
                                <i className="ri-ruler-line mr-1"></i>
                                {imovel.area}m²
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-lg font-bold text-orange-500">
                                  {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(imovel.preco)}
                                </p>
                              </div>
                              <button className="bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors text-xs whitespace-nowrap">
                                Ver Detalhes
                              </button>
                            </div>
                          </div>
                        </Link>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            toggleFavorito(imovel.id);
                          }}
                          className="absolute top-3 left-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg z-10"
                          title={
                            favoritos.includes(imovel.id) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'
                          }
                        >
                          <i
                            className={`${
                              favoritos.includes(imovel.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-700'
                            } text-xl`}
                          ></i>
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="text-center mt-12"
                >
                  <Link
                    to="/imoveis"
                    className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-full text-lg transition-all shadow-lg hover:shadow-orange-500/50 hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Ver Todos os Imóveis
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </motion.div>
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-home-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-xl text-gray-600">Nenhum imóvel em destaque no momento</p>
              </div>
            )}
          </div>
        </section>

        {/* 2. Busca Rápida */}
        <section className="py-16 bg-gradient-to-br from-orange-50 to-orange-100/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
                <i className="ri-search-line text-orange-500 mr-3"></i>
                Busca Rápida
              </h2>
              <p className="text-lg text-gray-600">Encontre o imóvel ideal em segundos</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-home-line mr-1 text-orange-500"></i>
                    Tipo de Imóvel
                  </label>
                  <select
                    value={buscaTipo}
                    onChange={(e) => setBuscaTipo(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="Casa">Casa</option>
                    <option value="Apartamento">Apartamento</option>
                    <option value="Terreno">Terreno</option>
                    <option value="Comercial">Comercial</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-map-pin-line mr-1 text-orange-500"></i>
                    Cidade
                  </label>
                  <select
                    value={buscaCidade}
                    onChange={(e) => setBuscaCidade(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Todas as cidades</option>
                    <option value="Aracaju">Aracaju</option>
                    <option value="Barra dos Coqueiros">Barra dos Coqueiros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-money-dollar-circle-line mr-1 text-orange-500"></i>
                    Preço Máximo
                  </label>
                  <select
                    value={buscaPrecoMax}
                    onChange={(e) => setBuscaPrecoMax(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Qualquer valor</option>
                    <option value="300000">Até R$ 300.000</option>
                    <option value="500000">Até R$ 500.000</option>
                    <option value="800000">Até R$ 800.000</option>
                    <option value="1000000">Até R$ 1.000.000</option>
                    <option value="2000000">Até R$ 2.000.000</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={handleBuscaRapida}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-search-2-line mr-2"></i>
                    Buscar
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 3. Tour Virtual 360° + Reels Instagram */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Conheça Nossos <span className="text-orange-500">Imóveis</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                Explore tours virtuais e vídeos dos nossos imóveis
              </p>

              {/* Tabs */}
              <div className="inline-flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('tours')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === 'tours'
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <i className="ri-vidicon-line mr-2"></i>
                  Tour Virtual 360°
                </button>
                <button
                  onClick={() => setActiveTab('reels')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === 'reels'
                      ? 'bg-gradient-to-r from-pink-500 to-orange-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <i className="ri-instagram-line mr-2"></i>
                  Reels Instagram
                </button>
              </div>
            </motion.div>

            {/* Tours Virtuais */}
            {activeTab === 'tours' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {videosDestaque.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {videosDestaque.slice(0, 2).map((video, index) => (
                      <motion.a
                        key={video.id}
                        href={video.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer"
                        style={{ height: '400px' }}
                      >
                        <img
                          src={getYouTubeThumbnail(video.youtube_url)}
                          alt={video.titulo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                            <i className="ri-play-fill text-4xl text-white ml-1"></i>
                          </div>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-2xl font-bold text-white mb-2">{video.titulo}</h3>
                          <p className="text-white/80 flex items-center gap-2">
                            <i className="ri-youtube-line text-red-500"></i>
                            Assistir no YouTube
                          </p>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {[
                      {
                        title: 'Apartamento Moderno',
                        image:
                          'https://readdy.ai/api/search-image?query=modern%20luxury%20apartment%20interior%20with%20large%20windows%20city%20view%20contemporary%20furniture%20elegant%20living%20room%20high-end%20finishes%20sophisticated%20design%20clean%20lines%20minimalist%20style%20premium%20quality&width=800&height=600&seq=tour1&orientation=landscape',
                      },
                      {
                        title: 'Casa de Luxo',
                        image:
                          'https://readdy.ai/api/search-image?query=luxury%20house%20exterior%20with%20beautiful%20garden%20swimming%20pool%20modern%20architecture%20elegant%20facade%20tropical%20landscaping%20palm%20trees%20sunset%20lighting%20premium%20property%20sophisticated%20design&width=800&height=600&seq=tour2&orientation=landscape',
                      },
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer"
                        style={{ height: '400px' }}
                      >
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                            <i className="ri-play-fill text-4xl text-white ml-1"></i>
                          </div>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                          <p className="text-white/80">Tour Virtual 360°</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-center mt-12"
                >
                  <Link
                    to="/videos"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Ver Todos os Tours
                    <i className="ri-arrow-right-line text-xl"></i>
                  </Link>
                </motion.div>
              </motion.div>
            )}

            {/* Reels Instagram */}
            {activeTab === 'reels' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {reelsInstagram.map((reel, index) => (
                    <motion.a
                      key={reel.id}
                      href={reel.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer aspect-[9/16]"
                    >
                      <img src={reel.thumbnail} alt={reel.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

                      <div className="absolute top-3 right-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-pink-500 via-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                          <i className="ri-play-fill text-white text-sm"></i>
                        </div>
                      </div>

                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <i className="ri-play-fill text-3xl text-white ml-1"></i>
                        </div>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-sm font-bold text-white line-clamp-2">{reel.titulo}</h3>
                        <p className="text-white/70 text-xs flex items-center gap-1 mt-1">
                          <i className="ri-instagram-line"></i>
                          Ver no Instagram
                        </p>
                      </div>
                    </motion.a>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-center mt-12"
                >
                  <a
                    href="https://www.instagram.com/mateusoliveira.corretor/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 text-white font-bold rounded-xl hover:opacity-90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-instagram-line text-xl"></i>
                    Seguir no Instagram
                    <i className="ri-arrow-right-line text-xl"></i>
                  </a>
                </motion.div>
              </motion.div>
            )}
          </div>
        </section>

        {/* 4. Por Que Escolher */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                Por Que Escolher <span className="text-orange-500">Mateus Oliveira</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Setenove Imóveis - Atendimento moderno, rápido e consultivo
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center bg-white rounded-2xl p-10 hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-24 h-24 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-award-line text-5xl text-orange-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Imobiliária Premium</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Especializada em imóveis de médio e alto padrão em Aracaju e Barra dos Coqueiros
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center bg-white rounded-2xl p-10 hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-24 h-24 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-whatsapp-line text-5xl text-green-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Atendimento Moderno</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Atendimento rápido e consultivo via WhatsApp, sem burocracia
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-center bg-white rounded-2xl p-10 hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-24 h-24 mx-auto mb-6 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-5xl text-teal-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Acompanhamento Total</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Da escolha do imóvel até o fechamento, incluindo financiamento e documentação
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. Mapa de Imóveis */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                Onde Estamos <span className="text-orange-500">Presentes</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Encontre imóveis disponíveis em Aracaju e Barra dos Coqueiros
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Mapa */}
              <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d124796.41974948!2d-37.12!3d-10.95!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x71ab17e95e0a3d7%3A0x6e0e0e0e0e0e0e0e!2sAracaju%2C%20SE!5e0!3m2!1spt-BR!2sbr!4v1699999999999!5m2!1spt-BR!2sbr"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Imóveis em Aracaju e Barra dos Coqueiros"
                  className="w-full h-[500px]"
                ></iframe>
              </div>

              {/* Lista de Cidades */}
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl p-6 border border-orange-200">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-fill text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Aracaju</h3>
                      <p className="text-gray-600">Capital de Sergipe</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Imóveis nos melhores bairros: Atalaia, Jardins, 13 de Julho, Grageru e mais.
                  </p>
                  <Link
                    to="/imoveis?cidade=Aracaju"
                    className="inline-flex items-center text-orange-500 font-semibold hover:text-orange-600 transition-colors cursor-pointer"
                  >
                    Ver imóveis em Aracaju
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl p-6 border border-teal-200">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-fill text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Barra dos Coqueiros</h3>
                      <p className="text-gray-600">Região Metropolitana</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Casas e apartamentos com vista para o mar e condomínios fechados de alto padrão.
                  </p>
                  <Link
                    to="/imoveis?cidade=Barra%20dos%20Coqueiros"
                    className="inline-flex items-center text-teal-500 font-semibold hover:text-teal-600 transition-colors cursor-pointer"
                  >
                    Ver imóveis na Barra
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <i className="ri-information-line text-2xl text-gray-500"></i>
                    <h4 className="font-semibold text-gray-800">Imóveis Disponíveis</h4>
                  </div>
                  <p className="text-3xl font-bold text-orange-500 mb-2">{todosImoveis.length}+</p>
                  <p className="text-gray-600 text-sm">imóveis cadastrados em nossa plataforma</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
